/**
 * Hash router for the dashboard shell (#92).
 *
 * Knows nothing about modules: it normalises the hash into a path, emits
 * path changes, and separates user navigation (push) from redirects
 * (replace). The shell decides what a path means.
 */

export interface HashEnv {
  getHash(): string;
  pushHash(hash: string): void;
  replaceHash(hash: string): void;
  onHashChange(listener: () => void): () => void;
}

export const browserHashEnv: HashEnv = {
  getHash: () => window.location.hash,
  pushHash: (hash) => {
    window.location.hash = hash;
  },
  replaceHash: (hash) => {
    window.location.replace(hash);
  },
  onHashChange: (listener) => {
    window.addEventListener('hashchange', listener);
    return () => window.removeEventListener('hashchange', listener);
  },
};

// A leading alphanumeric keeps '.' and '..' out; the rest covers folder ids
// (Files #123). Modules decide which paths they accept.
const SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/;

// A query (#140: '#/todo?document=<id>') is kept on the path; the shell
// resolves the route without it and hands it to the module.
const QUERY = /^[A-Za-z0-9._~:%=&-]{1,400}$/;

/**
 * '#/account/security' → '/account/security'; empty → '/'; malformed → null.
 */
export function parseHash(hash: string): string | null {
  const full = hash.startsWith('#') ? hash.slice(1) : hash;
  const split = full.indexOf('?');
  const raw = split === -1 ? full : full.slice(0, split);
  const query = split === -1 ? '' : full.slice(split + 1);
  if (query !== '' && !QUERY.test(query)) return null;
  const suffix = query === '' ? '' : `?${query}`;
  if (raw === '' || raw === '/') return query === '' ? '/' : null;
  if (!raw.startsWith('/')) return null;

  const segments = raw.slice(1).replace(/\/+$/, '').split('/');
  if (!segments.every((segment) => SEGMENT.test(segment))) return null;
  return `/${segments.join('/')}${suffix}`;
}

export type RouteListener = (path: string | null) => void;

export interface Router {
  current(): string | null;
  navigate(path: string): void;
  replace(path: string): void;
  onRouteChange(listener: RouteListener): () => void;
  start(): void;
  stop(): void;
}

export function createRouter(env: HashEnv = browserHashEnv): Router {
  const listeners = new Set<RouteListener>();
  let detach: (() => void) | null = null;

  const emit = (): void => {
    const path = parseHash(env.getHash());
    listeners.forEach((listener) => listener(path));
  };

  return {
    current: () => parseHash(env.getHash()),

    navigate(path) {
      if (parseHash(env.getHash()) === path) return;
      env.pushHash(`#${path}`);
    },

    replace(path) {
      env.replaceHash(`#${path}`);
    },

    onRouteChange(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    start() {
      if (detach) return;
      detach = env.onHashChange(emit);
      emit();
    },

    stop() {
      detach?.();
      detach = null;
    },
  };
}
